import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import moment from "moment";
import {
  getUserData,
  searchUser,
  getDaySearchCount,
  getLoginDetails,
} from "../../Service/apiService";
import "../../assets/css/UserList.css";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import { FiSearch, FiTrash2 } from "react-icons/fi";


// Filter options
const roleOption = [
  { value: "customer", label: "Customer" },
  { value: "trial", label: "Trial" },
  { value: "admin", label: "Admin" },
  { value: "lead", label: "Lead" },
];
const statusOption = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

// formatting date
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? "-" : moment(d).format("DD/MM/YYYY");
};
//login count
const getLoginCountForUser = (userId, loginDetailsResult) => {
  const userLogins = loginDetailsResult.find(item => item.userId === userId);
  return userLogins ? userLogins.logins.length : 0;
};

const getYearMonthDifference = (createdAt) => {
  if (!createdAt) return "-";
  const now = moment();
  const created = moment(createdAt);
  const years = now.diff(created, "years");
  created.add(years, "years");
  const months = now.diff(created, "months");
  return years === 0 ? `${months}M` : `${years}Y ${months}M`;
};

const formatRoleDisplay = (role) => {
  if (role === "admin") return "Admin";
  if (role === "trial") return "Trial";
  if (role === "search-admin") return "Search Admin";
  return "Customer";
};

const formatMobile = (mobile, countryCode) => {
  if (mobile == null) return "-";
  return countryCode ? `${countryCode} ${mobile}` : mobile;
};

const processUserData = (rawUsers = [], loginDetailsResult = []) => {
  return rawUsers.map((item) => {
    const firstLogin = item.logins?.[0] || {};
    const loginCount = getLoginCountForUser(item._id, loginDetailsResult);

    return {
      ...item,
      // Pre-formatted display values
      formattedName:
        item.name
          ?.split(" ")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(" ") || "-",
      formattedCompanyName: item.companyName?.toUpperCase() || "-",
      formattedCompanyType: item.companyType || "-",
      formattedPaymentStatus: item.paymentStatus || "-",
      formattedUpdatedAt: formatDate(item.updatedAt),
      formattedInvoiceDate: formatDate(item.invoiceDate),
      formattedLeadsDate: formatDate(item.leadsDate),
      formattedDemoDate: formatDate(item.demoDate),
      formattedPersonalMeetingDate: formatDate(item.personalMeetingDate),
      formattedSubStartDate: formatDate(item.subStartDate),
      formattedSubEndDate: formatDate(item.subEndDate),
      formattedCreatedAt: formatDate(item.createdAt),
      formattedCountry: item.country || "-",
      formattedDesignation: item.designation || "-",
      formattedEmail: item.email || "-",
      formattedMobile: formatMobile(item.mobile, item.countryCode),
      formattedIp: firstLogin.ip || "-",
      formattedCity: firstLogin.city || "-",
      formattedLatitude: firstLogin.latitude || "-",
      formattedLongitude: firstLogin.longitude || "-",
      formattedRole: formatRoleDisplay(item.role),
      formattedStatus: item.activeStatus ? "Active" : "Inactive",
      formattedSalesRep: item.salesRep || "-",
      formattedUserCreatedBy: item.userCreatedBy || "-",
      formattedActiveYears: getYearMonthDifference(item.createdAt),
      formattedActivityColor: item.activeStatus
        ? "rgb(124, 252, 0)"
        : "#FF0000",
      hasAdminRole: item.role === "admin",
      formattedCounts:
        item.role !== "admin"
          ? {
              search: item.searchCount || 0,
              excel: item.excelCount || 0,
              pdf: item.pdfCount || 0,
              logins: loginCount,
            }
          : null,
      formattedLoginCount: loginCount,
      formattedSearchCounts: {
        oneWeek: item.searchCounts?.oneWeekCount || 0,
        oneMonth: item.searchCounts?.oneMonthCount || 0,
        threeMonth: item.searchCounts?.threeMonthCount || 0,
        sixMonth: item.searchCounts?.sixMonthCount || 0,
        twelveMonth: item.searchCounts?.twelveMonthCount || 0,
      },
    };
  });
};


const UserList = () => {
  const selectRoleFilter = useRef();
  const selectStatusFilter = useRef();

  const [users, setUsers] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100;

  // Filters
  const [filterText, setFilterText] = useState("");
  const [filterStartValue, setFilterStartValue] = useState("");
  const [filterEndValue, setFilterEndValue] = useState("");

  // Initialize filter states as null
  const [filterStatusValue, setFilterStatusValue] = useState(null);
  const [filterRole, setFilterRole] = useState(null);
  const [updatedAtValue, setUpdatedAtValue] = useState("");
  const [monthSearch, setMonthSearch] = useState(false);

  // Industry config
  const configForHost = window.location.hostname.includes("pharma")
    ? { apiTitle: "Pharmaceuticals" }
    : { apiTitle: "General" };
  const isPharma = configForHost.apiTitle === "Pharmaceuticals";
  //const industry = !isPharma ? configForHost.apiTitle : "";
  const industry = "";

  // For Detailed Search Count Modal
  const [countModal, setCountModal] = useState({ status: false, data: [] });
  const [dayCountLoading, setDayCountLoading] = useState(false);

  // For IP Tracking Modal
  const [ipModal, setIpModal] = useState({ status: false, data: [] });
  const [ipTrackingLoading, setIpTrackingLoading] = useState(false);

  // --- Fetch Data ---
 const fetchData = async (page = 1) => {

  setLoading(true);
  try {
    const hasFilters = !!(
      filterText?.trim() ||
      filterStartValue ||
      filterEndValue ||
      updatedAtValue ||
      filterRole !== null ||
      filterStatusValue !== null ||
      monthSearch
    );

    let rawUsers = [];
    let totalCount = 0;

    if (hasFilters) {
      let queryString = "?userDeleted=false";
      if (filterStartValue) queryString += `&subStartDate=${filterStartValue}`;
      if (filterEndValue) queryString += `&subEndDate=${filterEndValue}`;
      if (updatedAtValue) queryString += `&lastUpdatedDate=${updatedAtValue}`;
      if (filterRole !== null) queryString += `&role=${filterRole}`;
      if (filterStatusValue !== null) {
        queryString += `&activeStatus=${filterStatusValue}`;
      }
      if (monthSearch) queryString += `&monthSearch=true`;

      const body = {
        name: filterText || undefined,
        industry: industry,
        page: page,
        limit: itemsPerPage,
      };

      Object.keys(body).forEach((k) => body[k] === undefined && delete body[k]);

      try {
        const response = await searchUser(queryString, body);
        rawUsers = response.result || [];
        totalCount = response.totalCount || 0;
      } catch (err) {
        if (err.message === "no data found") {
          rawUsers = [];
          totalCount = 0;
        } else {
          throw err;
        }
      }
    } else {
      const offset = (page - 1) * itemsPerPage;
      const response = await getUserData(offset, itemsPerPage, industry);
      rawUsers = response.result || [];
      totalCount = response.totalCount || 0;
    }

    // FETCH LOGIN DETAILS FOR ALL USER IDs IN THIS PAGE
    const userIds = rawUsers.map(u => u._id);
    let loginDetailsResult = [];
    if (userIds.length > 0) {
      try {
        const loginResponse = await getLoginDetails({ role: "admin", userIds }); // ← will adjust API below
        loginDetailsResult = loginResponse.result || [];
      } catch (err) {
        console.warn("Failed to fetch login details for display:", err);
        loginDetailsResult = [];
      }
    }

    // PROCESS USERS WITH LOGIN COUNTS
    setUsers(processUserData(rawUsers, loginDetailsResult));
    setTotalRecords(totalCount);
  } catch (err) {
    console.error("Fetch error:", err);
    setUsers([]);
    setTotalRecords(0);
    toast.error("Failed to load users");
  } finally {
    setLoading(false);
  }
};
  // --- Handlers ---
  const onChangeTextInput = (e) => {
    const { name, value } = e.target;
    if (name === "startFilter") setFilterText(value);
    if (name === "subStartDateFilter") setFilterStartValue(value);
    if (name === "subEndDateFilter") setFilterEndValue(value);
    if (name === "updatedAt") setUpdatedAtValue(value);
  };

  const getDailySearchCount = async (userId) => {
    setDayCountLoading(true);
    try {
      const response = await getDaySearchCount({ userId });
      if (response) {
        setCountModal({ status: true, data: response });
      } else {
        toast.error("No search count data available");
      }
    } catch (err) {
      console.error("Error fetching daily search count:", err);
      toast.error("Failed to load search count");
    } finally {
      setDayCountLoading(false);
    }
  };

  const getIpTracking = async (userId) => {
    setIpTrackingLoading(true);
    try {
      const response = await getLoginDetails({ role: "admin", userId }); // or your actual user role
      if (response?.result?.length > 0 && response.result[0]?.logins) {
        setIpModal({ status: true, data: response.result[0].logins });
      } else {
        toast.error("No IP tracking records found");
      }
    } catch (err) {
      console.error("Error fetching IP tracking:", err);
      toast.error("Failed to load IP tracking data");
    } finally {
      setIpTrackingLoading(false);
    }
  };

  const onRoleChangeOption = (opt) => setFilterRole(opt ? opt.value : null);
  const onStatusChangeOption = (opt) =>
    setFilterStatusValue(opt ? opt.value === "Active" : null);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchData(1);
  };

  const clearFilters = () => {
    selectRoleFilter.current?.select?.clearValue?.();
    selectStatusFilter.current?.select?.clearValue?.();
    setFilterText("");
    setFilterStartValue("");
    setFilterEndValue("");
    setUpdatedAtValue("");
    setFilterRole(null);
    setFilterStatusValue(null);
    setMonthSearch(false);
    setCurrentPage(1);
    fetchData(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchData(page);
  };

  // --- Initial Load ---
  useEffect(() => {
    fetchData(1);
  }, []);

  // --- Render Helpers ---
  const totalPages = Math.ceil(totalRecords / itemsPerPage);

   const SkeletonUserTable = ({ rows = 20 }) => (
     <div className="user-table-scroll-container">
       <table className="user-company-table">
         <thead>
           <tr>
             <th>Sr No.</th>
             <th>Name</th>
             <th>Company</th>
             <th>Email</th>
             <th>Role</th>
             <th>Status</th>
             <th>Active Since</th>
             <th>1W</th>
             <th>1M</th>
             <th>Logins</th>
             <th>🔍</th>
             <th>📍</th>
           </tr>
         </thead>
         <tbody>
           {Array.from({ length: rows }).map((_, i) => (
             <tr key={i}>
               <td><Skeleton width={30} height={20} /></td>
               <td><Skeleton width={100} height={20} /></td>
               <td><Skeleton width={120} height={20} /></td>
               <td><Skeleton width={140} height={20} /></td>
               <td><Skeleton width={60} height={20} /></td>
               <td><Skeleton width={50} height={20} /></td>
               <td><Skeleton width={70} height={20} /></td>
               <td><Skeleton width={40} height={20} /></td>
               <td><Skeleton width={40} height={20} /></td>
               <td><Skeleton width={40} height={20} /></td>
               <td><Skeleton circle height={22} width={22} /></td>
               <td><Skeleton circle height={22} width={22} /></td>
             </tr>
           ))}
         </tbody>
       </table>
     </div>
   );
 

  // Count Modal Component
  const CountModal = () => (
    <div
      className="custom-modal"
      style={{ display: countModal.status ? "block" : "none" }}
    >
      <div className="custom-modal-header">
        <h3>Day-Wise Search Count</h3>
        <button
          className="close-btn"
          onClick={() => setCountModal({ status: false, data: [] })}
        >
          ✖
        </button>
      </div>

      <div className="custom-modal-body">
        <div className="custom-card">
          <table className="custom-modal-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>Date</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {dayCountLoading ? (
                <tr>
                  <td colSpan="3" style={{ textAlign: "center" }}>
                    Loading...
                  </td>
                </tr>
              ) : countModal.data.length > 0 ? (
                countModal.data.map((item, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>{moment(item.date).format("DD-MM-YYYY")}</td>
                    <td>{item.count}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" style={{ textAlign: "center" }}>
                    <b>No data found</b>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // IP Tracking Modal Component
  const IpTrackingModal = () => (
    <div
      className="custom-modal"
      style={{ display: ipModal.status ? "block" : "none" }}
    >
       <div className="custom-modal-header">
        <h3>IP Tracking Details</h3>
        <button
          className="close-btn"
          onClick={() => setIpModal({ status: false, data: [] })}
        >
          ✖
        </button>
      </div>


      <div className="custom-modal-body">
        <div className="custom-card">
          <table className="custom-modal-table">
            <thead className="thead-light">
              <tr>
                <th>No.</th>
                <th>Date</th>
                <th>IP</th>
                <th>City</th>
                <th>Country</th>
                <th>Latitude</th>
                <th>Longitude</th>
              </tr>
            </thead>
            <tbody>
              {ipTrackingLoading ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>
                    Loading...
                  </td>
                </tr>
              ) : ipModal.data.length > 0 ? (
                ipModal.data.map((item, idx) => (
                  <tr key={item._id || idx}>
                    <td>{idx + 1}</td>
                    <td>{moment(item.createdAt).format("DD-MM-YYYY")}</td>
                    <td>{item.ip || "-"}</td>
                    <td>{item.city || "-"}</td>
                    <td>{item.country || "-"}</td>
                    <td>{item.latitude || "-"}</td>
                    <td>{item.longitude || "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>
                    <b>No data found</b>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
  return (
    <>
      {/*Render modals */}
    <CountModal />
    <IpTrackingModal />

    <div className="user-company-list-container">
      {/* Filters */}
      <form onSubmit={handleSearch} className="user-filter-form">
        <div className="user-filter-row">
          <div className="user-filter-group">
            <label>Search</label>
            <input
              type="text"
              name="startFilter"
              placeholder="Search user name, company, email"
              value={filterText}
              onChange={onChangeTextInput}
            />
          </div>

          {!monthSearch && (
            <div className="user-filter-group">
              <label>Subscription Start Date</label>
              <input
                type="date"
                name="subStartDateFilter"
                value={filterStartValue}
                onChange={onChangeTextInput}
              />
            </div>
          )}

          <div className="user-filter-group">
            <label>
              {monthSearch ? "Subscription End Month" : "Subscription End Date"}
            </label>
            <input
              type={monthSearch ? "month" : "date"}
              name="subEndDateFilter"
              value={filterEndValue}
              onChange={onChangeTextInput}
            />
          </div>

          <div className="user-wrapper-group">
            <label>Role</label>
            <Select
  ref={selectRoleFilter}
  options={roleOption}
  onChange={onRoleChangeOption}
  value={filterRole === null ? null : roleOption.find(opt => opt.value === filterRole)}
  isClearable
  menuPortalTarget={document.body}
  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
/>
          </div>

          <div className="user-wrapper-group">
            <label>Status</label>
          <Select
  ref={selectStatusFilter}
  options={statusOption}
  onChange={onStatusChangeOption}
  value={filterStatusValue === null ? null : statusOption.find(opt => opt.value === (filterStatusValue ? "Active" : "Inactive"))}
  isClearable
  menuPortalTarget={document.body}
  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
/>
          </div>

          {!monthSearch && (
            <div className="user-filter-group">
              <label>Last Updated</label>
              <input
                type="date"
                name="updatedAt"
                value={updatedAtValue}
                onChange={onChangeTextInput}
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </div>
          )}

          <div
            className="user-filter-group action-buttons"
            style={{ display: "flex", gap: "8px" }}
          >
           <button type="submit" className="user-btn-search">
  <FiSearch size={18} />
</button>

<button
  type="button"
  className="user-btn-clear"
  onClick={clearFilters}
>
  <FiTrash2 size={18} />
</button>
          </div>
        </div>
      </form>

      {/* Table */}
    {loading ? (
          <SkeletonUserTable rows={Math.min(itemsPerPage, 20)} /> // limit to 20 for performance
        ) : users.length > 0 ? (
          /* DATA: Real table (unchanged) */
          <div className="user-table-scroll-container">
            <table className="user-company-table">
              {/* ... your full 39-col real table */}
              <thead>
                <tr>
                  <th>Sr No.</th>
                  <th>Name</th>
                  <th>Company Name</th>
                  <th>Company Status</th>
                  <th>Payment Status</th>
                  <th>Last Updated</th>
                  <th>Paid Date</th>
                  <th>Leads Date</th>
                  <th>Demo</th>
                  <th>Demo Date</th>
                  <th>Personal Meeting</th>
                  <th>Personal Meeting Date</th>
                  <th>Country</th>
                  <th>Designation</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>IP Address</th>
                  <th>City</th>
                  <th>Latitude</th>
                  <th>Longitude</th>
                  <th>Role</th>
                  <th>Subscription Start Date</th>
                  <th>Subscription End Date</th>
                  <th>Status</th>
                  <th>Permissions</th>
                  <th>Sales Rep</th>
                  <th>Created By</th>
                  <th>Created Date</th>
                  <th>Active Years</th>
                  <th>Activity</th>
                  <th>Counts</th>
                  <th>1 Week Count</th>
                  <th>1 Month Count</th>
                  <th>3 Month Count</th>
                  <th>6 Month Count</th>
                  <th>12 Month Count</th>
                  <th>Active Since</th>
                  <th>Login Count</th> 
                  <th>View Detailed Search Count</th>
                  <th>View IP Tracking</th>
                </tr>
              </thead>
              <tbody>
                {users.map((item, index) => (
                  <tr key={item._id || index}>
                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td>{item.formattedName}</td>
                    <td>{item.formattedCompanyName}</td>
                    <td>{item.formattedCompanyType}</td>
                    <td>{item.formattedPaymentStatus}</td>
                    <td>{item.formattedUpdatedAt}</td>
                    <td>{item.formattedInvoiceDate}</td>
                    <td>{item.formattedLeadsDate}</td>
                    <td>{item.demo || "-"}</td>
                    <td>{item.formattedDemoDate}</td>
                    <td>{item.personalMeeting || "-"}</td>
                    <td>{item.formattedPersonalMeetingDate}</td>
                    <td>{item.formattedCountry}</td>
                    <td>{item.formattedDesignation}</td>
                    <td>{item.formattedEmail}</td>
                    <td>{item.formattedMobile}</td>
                    <td>{item.formattedIp}</td>
                    <td>{item.formattedCity}</td>
                    <td>{item.formattedLatitude}</td>
                    <td>{item.formattedLongitude}</td>
                    <td>{item.formattedRole}</td>
                    <td>{item.formattedSubStartDate}</td>
                    <td>{item.formattedSubEndDate}</td>
                    <td>{item.formattedStatus}</td>
                    <td>
                      {item.permissions && (
                        <div>
                          <input type="checkbox" readOnly disabled checked={item.permissions[0]?.import29 || false} /> Import 29<br />
                          <input type="checkbox" readOnly disabled checked={item.permissions[0]?.export29 || false} /> Export 29<br />
                          <input type="checkbox" readOnly disabled checked={item.permissions[0]?.import30 || false} /> Import 30<br />
                          <input type="checkbox" readOnly disabled checked={item.permissions[0]?.export30 || false} /> Export 30
                        </div>
                      )}
                    </td>
                    <td>{item.formattedSalesRep}</td>
                    <td>{item.formattedUserCreatedBy}</td>
                    <td>{item.formattedCreatedAt}</td>
                    <td>{item.formattedActiveYears}</td>
                    <td>
                      <i className="user-fas fa-circle" style={{ color: item.formattedActivityColor }}></i>
                    </td>
                    <td>
                      {item.hasAdminRole ? "-" : (
                        <div>
                          <span>Search = {item.formattedCounts.search}</span><br />
                          <span>Excel = {item.formattedCounts.excel}</span><br />
                          <span>PDF = {item.formattedCounts.pdf}</span><br />
                          <span>Logins = {item.formattedCounts.logins}</span>
                        </div>
                      )}
                    </td>
                    <td>{item.formattedSearchCounts.oneWeek}</td>
                    <td>{item.formattedSearchCounts.oneMonth}</td>
                    <td>{item.formattedSearchCounts.threeMonth}</td>
                    <td>{item.formattedSearchCounts.sixMonth}</td>
                    <td>{item.formattedSearchCounts.twelveMonth}</td>
                    <td>{item.formattedSubStartDate}</td>
                    <td>{item.formattedLoginCount}</td>
                    <td>
                      <div style={{ textAlign: "center" }}>
                        <i
                          className="fas fa-info-circle"
                          style={{ cursor: "pointer", fontSize: "1.2rem", color: "#053763" }}
                          onClick={() => getDailySearchCount(item._id)}
                        ></i>
                      </div>
                    </td>
                    <td>
                      <div style={{ textAlign: "center" }}>
                        <i
                          className="fas fa-info-circle"
                          style={{ cursor: "pointer", fontSize: "1.2rem", color: "#053763" }}
                          onClick={() => getIpTracking(item._id)}
                        ></i>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          /* ✅ EMPTY: No data */
          <div className="user-table-scroll-container">
            <table className="user-company-table">
              <tbody>
                <tr>
                  <td colSpan="39" className="user-no-data">
                    No users found
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

      {/* Pagination */}
      {totalRecords > 0 && !loading && (
        <div className="user-custom-pagination">
          <button
            className={`pagination-btn ${currentPage === 1 ? "disabled" : ""}`}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            ← Prev
          </button>
          <span className="user-page-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className={`pagination-btn ${
              currentPage === totalPages ? "disabled" : ""
            }`}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next →
          </button>
        </div>
      )}
    </div>
    </>
  );
};

export default UserList;
